import { Localized } from "@fluent/react";
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography
} from "@mui/material";
import { useCallback, useState } from "react";
import { archiveProject } from "../projectSlice";
import { showSuccess } from "../../notifications/notificationsSlice";
import { useAppDispatch } from "../../../app/hooks";
import { AppId, AppTextFieldHandler } from "../../../app/appTypes";
import { ArchiveButton } from "../../../app/components/ArchiveButton";
import { CancelButton } from "../../../app/components/CancelButton";

export interface ArchiveProjectDialogProps {
    id: AppId;
    onSuccess: (id: AppId) => void;
    onCancel: () => void;
}

export const ArchiveProjectDialog = ({ id, onSuccess, onCancel }: ArchiveProjectDialogProps) => {
    const dispatch = useAppDispatch();
    const [password, setPassword] = useState("");
    const [running, setRunning] = useState(false);
    const [serverError, setServerError] = useState("");

    const run = async () => {
        setRunning(true);

        try {
            const result = await dispatch(
                archiveProject({
                    id: id,
                    args: { password }
                })
            );

            if (archiveProject.fulfilled.match(result)) {
                dispatch(showSuccess("archived"));
                onSuccess(id);
            } else {
                let errorMsg = "error";

                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }

                setServerError(errorMsg);
            }
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : "Unexpected error";
            setServerError(errorMsg);
        } finally {
            setRunning(false);
        }
    };

    const handlePasswordFieldChange: AppTextFieldHandler = useCallback(e => {
        setPassword(e.target.value);
    }, []);

    return (
        <Dialog open={true} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Localized id="project-archive-title">
                    <span>Archiwizacja projektu</span>
                </Localized>
            </DialogTitle>

            <DialogContent>
                {/* opis co się stanie po archiwizacji */}
                <Typography variant="body2" color="text.secondary">
                    <Localized id="project-archive-description">
                        <span>
                            Archiwizacja spowoduje, że projekt zniknie z listy aktywnych projektów,
                            nie będzie można go dalej edytować, ale jego dane pozostaną zapisane
                            w systemie (np. w widoku archiwum) do celów historycznych i raportowych.
                        </span>
                    </Localized>
                </Typography>

                <Typography variant="body2" sx={{ mt: 1 }}>
                    <Localized id="project-archive-question">
                        <span>Czy na pewno chcesz zarchiwizować ten projekt?</span>
                    </Localized>
                </Typography>

                <TextField
                    sx={{ mt: 2 }}
                    fullWidth
                    autoFocus
                    type="password"
                    value={password}
                    onChange={handlePasswordFieldChange}
                    label={
                        <Localized id="password">
                            <span>Hasło</span>
                        </Localized>
                    }
                    helperText={
                        <Localized id="autorization-password-required">
                            <span>Wymagana autoryzacja hasłem</span>
                        </Localized>
                    }
                    autoComplete="off"
                />

                {serverError && (
                    <Alert
                        sx={{ mt: 1 }}
                        onClose={() => {
                            setServerError("");
                        }}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <ArchiveButton loading={running} onClick={run} />
                <CancelButton loading={running} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
